import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Play, Volume2, Captions, CaptionsOff, Download, Mic2 } from "lucide-react";
import { AD_FILMS, api } from "@/lib/api";
import SubtitleStrip from "@/components/video/SubtitleStrip";
import FilmChips from "@/components/video/FilmChips";
import VoiceStudio from "@/components/video/VoiceStudio";

const Sources = ({ film }) => (
  <>
    <source src={film.src} type="video/mp4" />
    <source src={film.webm} type="video/webm" />
  </>
);

/**
 * Rotating ad-film series: autoplays (muted) and hands over to the next film when one ends.
 * Clicking opens a modal player with sound, subtitles, film selector, 4K master download and voice studio.
 */
export default function VideoShowcase({ films = AD_FILMS, startIndex = 0, label = "Autoplay Ad Film", hint = "Tap for Sound", testid = "video-showcase" }) {
  const [idx, setIdx] = useState(startIndex % films.length);
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState(true);
  const [studio, setStudio] = useState(false);
  const [narrators, setNarrators] = useState({});
  const cardRef = useRef(null);
  const modalRef = useRef(null);
  const film = films[idx];
  const next = () => setIdx((i) => (i + 1) % films.length);
  // actual narrator carried by the current render (background re-renders may lag the configured one)
  const rendered = narrators[film.id];
  const narrator = rendered ? rendered[0].toUpperCase() + rendered.slice(1) : film.narrator;

  useEffect(() => {
    api.get("/media/status").then((r) => setNarrators(r.data.narrators || {})).catch(() => {});
  }, []);

  const toggleModal = (v) => {
    setOpen(v);
    if (v) cardRef.current?.pause();
    else {
      setStudio(false);
      cardRef.current?.play().catch(() => {});
    }
  };

  return (
    <>
      <div
        className="relative rounded-2xl overflow-hidden border border-[#00F0FF]/15 d1-pulse-cyan group cursor-pointer"
        onClick={() => toggleModal(true)}
        data-testid={testid}
        role="button"
        aria-label={`Play ${film.title} advertisement with sound`}
      >
        <div className="aspect-[16/9] w-full relative bg-[#02050F]">
          <video
            key={`card-${film.id}`}
            ref={cardRef}
            className="absolute inset-0 w-full h-full object-cover"
            poster={film.poster}
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={() => !open && next()}
            data-testid={`${testid}-autoplay`}
            data-film={film.id}
          >
            <Sources film={film} />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-[#061536] via-[#061536]/20 to-transparent pointer-events-none" />
          <SubtitleStrip key={`sub-${film.id}`} lines={film.subtitles} cadence={film.cadence} active={subs && !open} testid={`${testid}-subtitles`} />
          <div className="absolute top-4 right-4">
            <FilmChips films={films} index={idx} onSelect={setIdx} compact testid={`${testid}-films`} />
          </div>
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <span className="w-16 h-16 rounded-full bg-[#FF2E2E]/90 flex items-center justify-center shadow-xl shadow-black/40">
              <Play className="w-7 h-7 text-white fill-white ml-1" />
            </span>
          </div>
          <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="w-3 h-3 rounded-full bg-[#FF2E2E] animate-pulse shrink-0" />
              <span className="font-mono-lbl text-xs tracking-wider uppercase text-white/90 truncate">
                {label}
                <span className="hidden sm:inline"> · {hint}</span>
              </span>
            </div>
            <span className="font-mono-lbl text-[11px] bg-[#061536]/80 border border-[#00F0FF]/40 text-[#00F0FF] px-3 py-1 rounded-full whitespace-nowrap" data-testid={`${testid}-now-playing`}>
              {film.title} · {idx + 1}/{films.length}
            </span>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={toggleModal}>
        <DialogContent className="max-w-4xl bg-[#02050F] border border-[#00F0FF]/15 p-2 sm:p-3" data-testid={`${testid}-modal`}>
          <DialogTitle className="sr-only">LuxeRay Advertisement Film — {film.title}</DialogTitle>
          <DialogDescription className="sr-only">Plays the {film.title} film with sound; switch films, toggle subtitles, download the 4K master or audition brand voices.</DialogDescription>
          <div className="relative">
            <video
              key={`modal-${film.id}`}
              ref={modalRef}
              className="w-full aspect-video rounded-lg bg-black"
              poster={film.poster}
              controls
              autoPlay
              playsInline
              onEnded={next}
              data-testid={`${testid}-modal-player`}
              data-film={film.id}
            >
              <Sources film={film} />
            </video>
            <SubtitleStrip key={`msub-${film.id}`} lines={film.subtitles} cadence={film.cadence} active={subs && open} testid={`${testid}-modal-subtitles`} />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-2 pt-1">
            <p className="font-mono-lbl text-[11px] text-white/60 flex items-center gap-2 uppercase tracking-wider" data-testid={`${testid}-modal-meta`}>
              <Volume2 className="w-3.5 h-3.5 text-[#00F0FF]" /> Sound on · {film.title}
              {narrator && <span className="text-[#FF1E8C]">· Narrated by {narrator}</span>}
            </p>
            <FilmChips films={films} index={idx} onSelect={setIdx} testid={`${testid}-modal-films`} />
            <button
              onClick={() => setSubs(!subs)}
              className="font-mono-lbl text-[11px] text-[#00F0FF] flex items-center gap-1.5 uppercase tracking-wider hover:text-white transition-colors"
              data-testid={`${testid}-subtitles-toggle`}
            >
              {subs ? <Captions className="w-3.5 h-3.5" /> : <CaptionsOff className="w-3.5 h-3.5" />}
              {subs ? "Subtitles On" : "Subtitles Off"}
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 px-2 pb-1">
            <a
              href={film.master4k}
              download={`LuxeRay-${film.id}-4K.mp4`}
              className="font-mono-lbl text-[11px] text-white flex items-center gap-1.5 uppercase tracking-wider bg-[#FF2E2E] hover:bg-[#E01F1F] px-3 py-1.5 rounded-full transition-colors"
              data-testid={`${testid}-download-4k`}
            >
              <Download className="w-3.5 h-3.5" /> Download 4K Master · 3840×2160
            </a>
            <button
              onClick={() => setStudio(!studio)}
              className="font-mono-lbl text-[11px] text-[#FF1E8C] flex items-center gap-1.5 uppercase tracking-wider hover:text-white transition-colors"
              data-testid={`${testid}-voice-studio-toggle`}
              aria-expanded={studio}
            >
              <Mic2 className="w-3.5 h-3.5" /> {studio ? "Hide Voice Studio" : "Brand Voice Studio"}
            </button>
          </div>
          {studio && <VoiceStudio onAudition={() => modalRef.current?.pause()} testid={`${testid}-voice-studio`} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
